import React, { useState } from 'react';
import { NextCta } from '../Layout';
import { FadeLink, Section, cx, useCopy } from '../core';
import academicIllustration from '../assets/img/partner-academic.webp';
import industryIllustration from '../assets/img/partner-industry.webp';
import investmentIllustration from '../assets/img/partner-investment.webp';
import eventReception from '../assets/img/event-reception.webp';
import eventStage from '../assets/img/event-stage.webp';
import eventTitleSlide from '../assets/img/event-title-slide.webp';

const KTO_RECORD = 'https://kto.cuhk.edu.hk/en/news-events/announcements/event-highlight-mix-and-mingle-venture-connect-for-ai-robotics';

const copy = {
  en: {
    pill: 'PARTNERSHIPS', title: <>Advance AI through<br />collaboration.</>,
    intro: 'A place to connect for universities, research institutions, industry partners and investors.',
    start: 'Start a conversation', label: 'Three ways to partner', filterLabel: 'Filter partnership paths',
    filters: [['all', 'All'], ['academic', 'Academic'], ['industry', 'Industry'], ['investment', 'Investment']],
    showingAll: 'Showing all partnership paths', highlighting: (name) => `Highlighting ${name} partnerships`,
    enquire: 'Enquire',
    paths: [
      ['academic', 'research', 'CONNECT RESEARCH WITH PRACTICE', 'Academic', 'Explore joint research, prototype validation and AI applications in education.', 'Concept illustration: joint research — a prototype-validation draft with steps from research question to pilot in education'],
      ['industry', 'industry', 'CONNECT AI WITH YOUR BUSINESS', 'Industry', 'Discuss product integration, application pilots and custom engineering for a specific use case.', 'Concept illustration: your systems connected to a specific application pilot through one integration'],
      ['investment', 'investment', 'EXPLORE THE NEXT OPPORTUNITY', 'Strategic & investment', 'Speak with our business team about product direction, market opportunities and strategic collaboration.', 'Concept illustration: a portfolio overview and a business-team meeting agenda']
    ],
    sources: 'PUBLIC EVENT SOURCES', activities: <>Explore our public<br />activities.</>,
    ticketLabel: 'PUBLIC EVENT', ticketDate: '22 Sep 2026', eventDate: 'PUBLIC EVENT · 22 SEPTEMBER 2026',
    event: 'CUHK KTO Venture Connect',
    eventText: 'AgentecFusion Limited participated in the AI & Robotics Venture Connect event and pitching session co-hosted by CUHK KTO and HKSTP.',
    record: 'Read the KTO event record',
    disclaimer: 'This records event participation, without implying university endorsement or a formal partnership.',
    photos: ['Reception area at Hong Kong Science Park (HKSTP) during the event', 'Venture Connect for AI & Robotics presentation on stage', 'AgentecFusion title slide on the event screen']
  },
  zh: {
    pill: '合作', title: <>通过合作，<br />推进 AI 应用。</>,
    intro: '为高校、研究机构、产业伙伴和投资者提供清晰的合作入口。',
    start: '开启对话', label: '三种合作方式', filterLabel: '筛选合作方式',
    filters: [['all', '全部'], ['academic', '学术'], ['industry', '产业'], ['investment', '投资']],
    showingAll: '显示全部合作方式', highlighting: (name) => `突出显示「${name}」合作`,
    enquire: '洽谈合作',
    paths: [
      ['academic', 'research', '把研究带入应用', '学术合作', '探讨联合研究、研究原型验证和教育场景中的 AI 应用。', '概念示意：联合研究——从研究问题到教育场景试点的原型验证草案'],
      ['industry', 'industry', '让 AI 连接实际业务', '产业合作', '围绕具体场景，探讨产品集成、应用试点和定制工程服务。', '概念示意：您的系统通过一次集成连接到具体的应用试点'],
      ['investment', 'investment', '一起探索发展机会', '战略与投资', '与商务团队交流产品方向、市场机会与战略合作。', '概念示意：产品组合概览与商务团队会议议程']
    ],
    sources: '公开活动资料', activities: <>了解我们的<br />公开活动。</>,
    ticketLabel: '公开活动', ticketDate: '2026年9月22日', eventDate: '公开活动 · 2026年9月22日',
    event: 'CUHK KTO Venture Connect',
    eventText: 'AgentecFusion Limited 参与了由香港中文大学知识转移处与香港科技园公司联合举办的 AI 与机器人 Venture Connect 活动及项目路演。',
    record: '查看 KTO 公开记录',
    disclaimer: '此处仅记录活动参与情况，不代表大学背书或正式合作关系。',
    photos: ['活动期间香港科学园（HKSTP）的接待区', 'AI 与机器人 Venture Connect 舞台演示', '活动屏幕上的 AgentecFusion 标题页']
  }
};

const ILLUSTRATIONS = [academicIllustration, industryIllustration, investmentIllustration];

export default function PartnershipsPage() {
  const { t } = useCopy(copy);
  const [filter, setFilter] = useState('all');
  const filterName = t.filters.find(([key]) => key === filter)?.[1];
  return <>
    <section className="pa" aria-labelledby="h1">
      <div className="wrap">
        <div className="copy hin">
          <span className="ppill" style={{ '--i': 0 }}><i />{t.pill}</span>
          <h1 id="h1" style={{ '--i': 1 }}>{t.title}</h1>
          <p className="intro" style={{ '--i': 2 }}>{t.intro}</p>
          <FadeLink className="btn btn-dark start" to="/contact" style={{ '--i': 3 }}>{t.start} <span className="ar" aria-hidden="true">↗</span></FadeLink>
        </div>
        <Section as="div">
          <div className="phead" data-reveal="">
            <p className="lbl">{t.label}</p>
            <div className="filters" role="group" aria-label={t.filterLabel}>
              {t.filters.map(([key, label]) => <button key={key} type="button" aria-pressed={filter === key} onClick={() => setFilter(key)}>{label}</button>)}
            </div>
          </div>
          <p className="fstatus" aria-live="polite">{filter === 'all' ? t.showingAll : t.highlighting(filterName)}</p>
          <div className={cx('pcs', filter !== 'all' && 'filtered')}>
            {t.paths.map(([key, type, kicker, title, text, alt], index) => (
              <FadeLink key={key} className={cx('pc', filter === key && 'match')} to={`/contact?type=${type}`} data-reveal="" style={{ '--i': index + 1 }}>
                <img className="ill" src={ILLUSTRATIONS[index]} alt={alt} loading="lazy" />
                <span className="k mono-k">{kicker}</span><h3>{title}</h3>
                <p>{text}</p>
                <span className="link">{t.enquire} <span className="ar" aria-hidden="true">↗</span></span>
              </FadeLink>
            ))}
          </div>
        </Section>
      </div>
    </section>
    <Section className="pub" id="activities">
      <div className="wrap">
        <div>
          <span className="ptag2" data-reveal="" style={{ '--i': 0 }}>{t.sources}</span>
          <h2 data-reveal="" style={{ '--i': 1 }}>{t.activities}
            <span className="ticket" aria-hidden="true"><span className="stub"><svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></svg></span><span className="tx"><small>{t.ticketLabel}</small><b>{t.ticketDate}</b></span></span></h2>
          <div data-reveal="" style={{ '--i': 2 }}>
            <p className="edate mono-k">{t.eventDate}</p>
            <h3>{t.event}</h3>
            <p className="desc">{t.eventText}</p>
            <a className="kto" href={KTO_RECORD} target="_blank" rel="noopener noreferrer"><i aria-hidden="true">↗</i>{t.record}</a>
            <p className="disc">{t.disclaimer}</p>
          </div>
        </div>
        <div className="mosaic">
          {[eventReception, eventStage, eventTitleSlide].map((src, index) => <figure key={src} className={`ph ph${index + 1}`}><img src={src} alt={t.photos[index]} loading="lazy" /></figure>)}
        </div>
      </div>
    </Section>
    <NextCta />
  </>;
}
